import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  buildContentPayload,
  getAdditionalSectionsFromContent,
  normalizeSections,
} from '../../utils/servicePageBody';

const SECTION_TYPES = [
  { value: 'cards', label: 'Cards Grid' },
  { value: 'features', label: 'Feature Highlights' },
  { value: 'documents', label: 'Document Lists' },
  { value: 'list', label: 'Bullet List' },
  { value: 'cta', label: 'Call To Action' },
];

const createEmptyItem = () => ({ title: '', text: '', listItems: [''] });
const createEmptySection = () => ({
  title: '',
  type: 'cards',
  items: [createEmptyItem()],
  listItems: [''],
  paragraphs: [''],
});

const listToText = (items = []) => items.join('\n');
const textToList = (value = '') => value
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean);

const ServicePageBodyEditor = ({ content, onChange }) => {
  const [sections, setSections] = useState(() => normalizeSections(getAdditionalSectionsFromContent(content)));

  useEffect(() => {
    setSections(normalizeSections(getAdditionalSectionsFromContent(content)));
  }, [content]);

  const emitChange = (nextSections) => {
    const normalized = normalizeSections(nextSections);
    setSections(normalized);
    onChange(buildContentPayload(normalized));
  };

  const updateSection = (index, patch) => {
    const next = sections.map((section, i) => (i === index ? { ...section, ...patch } : section));
    emitChange(next);
  };

  const updateSectionItem = (sectionIndex, itemIndex, patch) => {
    const next = sections.map((section, i) => {
      if (i !== sectionIndex) return section;
      const items = section.items.map((item, j) => (j === itemIndex ? { ...item, ...patch } : item));
      return { ...section, items };
    });
    emitChange(next);
  };

  const addSection = () => emitChange([...sections, createEmptySection()]);
  const removeSection = (index) => emitChange(sections.filter((_, i) => i !== index));

  const addItem = (sectionIndex) => {
    const next = sections.map((section, i) => (
      i === sectionIndex ? { ...section, items: [...section.items, createEmptyItem()] } : section
    ));
    emitChange(next);
  };

  const removeItem = (sectionIndex, itemIndex) => {
    const next = sections.map((section, i) => {
      if (i !== sectionIndex) return section;
      const items = section.items.filter((_, j) => j !== itemIndex);
      return { ...section, items: items.length ? items : [createEmptyItem()] };
    });
    emitChange(next);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2">
          Additional Sections (below the service boxes)
        </Typography>
        <Button startIcon={<AddIcon />} onClick={addSection} size="small">
          Add Section
        </Button>
      </Box>

      <Stack spacing={2}>
        {sections.map((section, sectionIndex) => (
          <Paper key={`section-${sectionIndex}`} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Section {sectionIndex + 1}
              </Typography>
              <IconButton color="error" onClick={() => removeSection(sectionIndex)} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <Stack spacing={2}>
              <TextField
                label="Section Title"
                value={section.title}
                onChange={(e) => updateSection(sectionIndex, { title: e.target.value })}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Section Layout</InputLabel>
                <Select
                  value={section.type}
                  label="Section Layout"
                  onChange={(e) => updateSection(sectionIndex, { type: e.target.value })}
                >
                  {SECTION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {section.type === 'list' && (
                <TextField
                  label="List Items (one per line)"
                  value={listToText(section.listItems)}
                  onChange={(e) => updateSection(sectionIndex, { listItems: textToList(e.target.value) })}
                  fullWidth
                  multiline
                  rows={6}
                />
              )}

              {section.type === 'cta' && (
                <TextField
                  label="Paragraphs (one per line)"
                  value={listToText(section.paragraphs)}
                  onChange={(e) => updateSection(sectionIndex, { paragraphs: textToList(e.target.value) })}
                  fullWidth
                  multiline
                  rows={5}
                  helperText="Last short line can be used as button/label text on the public page."
                />
              )}

              {(section.type === 'cards' || section.type === 'features' || section.type === 'documents') && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Cards / Items
                    </Typography>
                    <Button startIcon={<AddIcon />} onClick={() => addItem(sectionIndex)} size="small">
                      Add Item
                    </Button>
                  </Box>

                  <Stack spacing={2}>
                    {section.items.map((item, itemIndex) => (
                      <Paper key={`item-${itemIndex}`} sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Item {itemIndex + 1}
                          </Typography>
                          <IconButton color="error" onClick={() => removeItem(sectionIndex, itemIndex)} size="small">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Stack spacing={2}>
                          <TextField
                            label="Title"
                            value={item.title}
                            onChange={(e) => updateSectionItem(sectionIndex, itemIndex, { title: e.target.value })}
                            fullWidth
                          />
                          <TextField
                            label="Description"
                            value={item.text}
                            onChange={(e) => updateSectionItem(sectionIndex, itemIndex, { text: e.target.value })}
                            fullWidth
                            multiline
                            rows={3}
                          />
                          {section.type === 'documents' || section.type === 'cards' ? (
                            <TextField
                              label="Bullet Points (one per line, optional)"
                              value={listToText(item.listItems)}
                              onChange={(e) => updateSectionItem(sectionIndex, itemIndex, { listItems: textToList(e.target.value) })}
                              fullWidth
                              multiline
                              rows={4}
                            />
                          ) : null}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>

      {!sections.length && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No page sections yet. Add sections to match the public page layout.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addSection}>
            Add First Section
          </Button>
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Hero, intro title, and intro text are edited above. Service boxes and additional sections below control the public page body.
      </Typography>
    </Box>
  );
};

export default ServicePageBodyEditor;
